import { DefaultLabelFormatterCallbackParams } from "echarts";
import { LineChart, LineSeriesOption } from "echarts/charts";
import {
  GridComponent,
  GridComponentOption,
  TitleComponentOption,
  TooltipComponent,
  TooltipComponentOption,
} from "echarts/components";
import * as echarts from "echarts/core";
import { ComposeOption } from "echarts/core";
import { CanvasRenderer } from "echarts/renderers";
import ReactECharts from "echarts-for-react";
import { TFunction } from "i18next";
import { useRef } from "react";
import { renderToString } from "react-dom/server";
import { useTranslation } from "react-i18next";

import ChartTooltip from "@/components/ui/ChartTooltip";
import { CategoryStyles } from "@/config";
import {
  TCarbonUnit,
  TCategoryProjections,
  TTrajectoryCategory,
} from "@/types";
import { formatCarbon } from "@/utils/carbon";
import { encodeSvgToDataURI, SYMBOL_SVG } from "@/utils/chart";

import colors from "../../../../colors";
import ProjectionsChartLegend, {
  ProjectionsChartLegendRef,
} from "./ProjectionsChartLegend";

echarts.use([GridComponent, TooltipComponent, LineChart, CanvasRenderer]);

export type ECOption = ComposeOption<
  | LineSeriesOption
  | TitleComponentOption
  | TooltipComponentOption
  | GridComponentOption
>;

export const CategoryColors: { [key: string]: string } = {
  office: CategoryStyles["office"].color,
  operations: CategoryStyles["operation"].color,
  support: CategoryStyles["support"].color,
  transport: CategoryStyles["transport"].color,
  usage: CategoryStyles["usage"].color,
  other: CategoryStyles["other"].color,
};

function optionFactory(
  categoryProjections: TCategoryProjections,
  t: TFunction
): ECOption {
  const years = Object.keys(Object.values(categoryProjections)[0]);

  return {
    textStyle: {
      fontFamily: "Poppins",
    },
    grid: {
      left: "3%",
      right: "3%",
      bottom: "0%",
      top: "10%",
      containLabel: true,
    }, // Configure the grid
    xAxis: {
      type: "category",
      boundaryGap: false,
      data: years,
      axisLine: {
        show: true,
        lineStyle: { color: colors["grayscale"]["x-light"] },
      },
      axisLabel: { color: colors["grayscale"].DEFAULT },
    },
    yAxis: {
      type: "value",
      name: t("common:unit.tCO2e"),
      nameTextStyle: { color: colors["grayscale"].DEFAULT, align: "right" },
      nameGap: 16,
      axisLine: {
        show: true,
        lineStyle: { color: colors["grayscale"]["x-light"] },
      },
      axisLabel: {
        color: colors["grayscale"].DEFAULT,
        formatter: (value) => t("formatter:number", { value }),
      },
      splitLine: {
        lineStyle: {
          type: "dashed",
        },
      },
    },
    tooltip: {
      borderWidth: 0,
      padding: 0,
      extraCssText: "border-radius: 8px;",
      formatter: ({
        name,
        seriesName,
        data,
      }: DefaultLabelFormatterCallbackParams) => {
        const { formattedCarbon, unit } = formatCarbon(
          t,
          data as number,
          TCarbonUnit.tCO2e
        );
        return renderToString(
          <ChartTooltip
            seriesName={`${name} - ${t(`categories:${seriesName}`)}`} // year - category
            valueToDisplay={formattedCarbon}
            unit={t(`common:unit.${unit}`)}
          />
        );
      },
    } as TooltipComponentOption,
    series: Object.keys(categoryProjections).map((category) => {
      const color = CategoryColors[category];

      return {
        name: category,
        type: "line",
        stack: "Total",
        smooth: true,
        color,
        symbol: encodeSvgToDataURI(SYMBOL_SVG, color),
        symbolSize: 12,
        lineStyle: { color, width: 1 },
        areaStyle: {
          color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
            {
              offset: 0,
              // here {color} is the hexadecimal code of the color (RGB format), followed by an alpha value, it becomes an RGBA format
              color: `${color}4D`, // opacity 77% (4D base-16 = 77 base-10)
            },
            {
              offset: 1,
              color: `${color}0D`, // opacity 13% (0D base-16 = 13 base-10)
            },
          ]),
        },
        emphasis: {
          focus: "series",
        },
        triggerLineEvent: true,
        data: Object.values(
          categoryProjections[category as TTrajectoryCategory]
        ),
      };
    }),
  } satisfies ECOption;
}

type ProjectionsChartProps = {
  categoryProjections: TCategoryProjections;
};

function ProjectionsChart({ categoryProjections }: ProjectionsChartProps) {
  const echartsRef = useRef<ReactECharts>(null);
  const legendRef = useRef<ProjectionsChartLegendRef>(null);
  const { t } = useTranslation(["categories", "common", "formatter"]);

  const handleChartHover = (category: string | undefined) => {
    legendRef.current?.highlight(category);
  };

  const handleLegendHover = (category: string | undefined) => {
    if (category) {
      echartsRef.current?.getEchartsInstance().dispatchAction({
        type: "highlight",
        seriesName: category,
      });
    } else {
      echartsRef.current?.getEchartsInstance().dispatchAction({
        type: "downplay",
      });
    }
  };

  const option = optionFactory(categoryProjections, t);

  return (
    <div className="gap-y-xs flex flex-col">
      <ReactECharts
        echarts={echarts}
        option={option}
        ref={echartsRef}
        onEvents={{
          mouseover: ({ seriesName }: { seriesName: string }) =>
            handleChartHover(seriesName),
          mouseout: () => handleChartHover(undefined),
        }}
      />
      <ProjectionsChartLegend
        categoryProjections={categoryProjections}
        ref={legendRef}
        onHover={handleLegendHover}
      />
    </div>
  );
}

export default ProjectionsChart;
